import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { pulizieApi, erroreApi } from './api';
import { RigaAttivita } from './RigaAttivita';

const GiorniChiusura = ({ piano, onSalva }) => (
  <div data-testid="pulizie-chiusura">
    <p className="text-sm font-medium">Giorni di chiusura</p>
    <p className="text-xs text-gray-500 mb-2">In questi giorni le pulizie giornaliere non risultano saltate.</p>
    <div className="flex flex-wrap gap-1.5">
      {piano.giorni.map((g, i) => {
        const on = piano.giorni_chiusura.includes(i);
        return (
          <button key={g} disabled={!piano.puo_modificare} data-testid={`pulizie-chiusura-${g}`}
            onClick={() => onSalva(on ? piano.giorni_chiusura.filter((x) => x !== i) : [...piano.giorni_chiusura, i])}
            className={`w-11 h-9 rounded-lg text-xs font-medium border capitalize transition-colors ${on ? 'bg-slate-900 border-slate-900 text-white' : 'border-gray-300 hover:bg-gray-100'}`}>
            {g}
          </button>
        );
      })}
    </div>
  </div>
);

const NuovaAttivita = ({ zone, onAggiungi }) => {
  const [zona, setZona] = useState('');
  const [attivita, setAttivita] = useState('');
  const aggiungi = async () => { if (await onAggiungi({ zona, attivita })) setAttivita(''); };
  return (
    <div className="rounded-xl border border-dashed border-gray-300 p-3 space-y-2" data-testid="pulizie-piano-nuova">
      <p className="text-sm font-medium">Aggiungi un'attività</p>
      <Input list="pulizie-zone" value={zona} onChange={(e) => setZona(e.target.value)} placeholder="Zona (es. Cucina o una nuova)"
        data-testid="pulizie-piano-nuova-zona" />
      <datalist id="pulizie-zone">{zone.map((z) => <option key={z} value={z} />)}</datalist>
      <Input value={attivita} onChange={(e) => setAttivita(e.target.value)} placeholder="Cosa si pulisce" data-testid="pulizie-piano-nuova-attivita" />
      <Button onClick={aggiungi} disabled={!zona.trim() || !attivita.trim()} variant="outline" className="w-full" data-testid="pulizie-piano-nuova-salva">
        <Plus className="h-4 w-4 mr-1" />Aggiungi
      </Button>
    </div>
  );
};

export const PianoDialog = ({ open, onClose, onCambiato }) => {
  const [piano, setPiano] = useState(null);
  const carica = useCallback(async () => {
    try { setPiano(await pulizieApi.get('/piano')); } catch (e) { toast.error(erroreApi(e, 'Piano non disponibile')); }
  }, []);
  useEffect(() => { if (open) carica(); }, [open, carica]);

  const esegui = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await carica(); onCambiato(); return true; } catch (e) { toast.error(erroreApi(e, 'Modifica non salvata')); return false; }
  };
  const cambia = (a, campi) => esegui(() => pulizieApi.put(`/piano/${a.id}`, campi));
  const elimina = (a) => window.confirm(`Togliere "${a.attivita}" dal piano?`) && esegui(() => pulizieApi.del(`/piano/${a.id}`), 'Attività tolta');
  const aggiungi = (body) => esegui(() => pulizieApi.post('/piano', { ...body, frequenza: 'giornaliera', tipo_prodotto: 'detergente' }), 'Attività aggiunta');
  const chiusura = (giorni) => esegui(() => pulizieApi.put('/impostazioni', { giorni_chiusura: giorni }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="pulizie-piano-dialog">
        <DialogHeader>
          <DialogTitle>Piano di pulizia</DialogTitle>
          <DialogDescription>Cosa si pulisce, quando e con quale prodotto. I prodotti arrivano dalle tue fatture.</DialogDescription>
        </DialogHeader>
        {!piano ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /> : (
          <div className="space-y-5">
            {!piano.puo_modificare && <p className="text-xs text-amber-700">Solo il responsabile può cambiare il piano.</p>}
            <GiorniChiusura piano={piano} onSalva={chiusura} />
            {piano.zone.map((z) => (
              <div key={z.zona}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{z.zona}</h4>
                <ul>{z.attivita.map((a) => (
                  <RigaAttivita key={a.id} a={a} frequenze={piano.frequenze} tipi={piano.tipi} prodotti={piano.prodotti}
                    modificabile={piano.puo_modificare}
                    onCambia={cambia} onElimina={elimina} />
                ))}</ul>
              </div>
            ))}
            {piano.puo_modificare && <NuovaAttivita zone={piano.zone.map((z) => z.zona)} onAggiungi={aggiungi} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
